/**
 * Утилиты для форматирования дат
 */

import { monthNames } from './constants'

/**
 * Форматирует дату и время для ссылок оплаты
 * @param {Date} date - объект даты
 * @param {string} locale - локаль (по умолчанию 'ru')
 * @returns {string} - форматированная строка даты
 */
export function formatPaymentLinkDate(date, locale = 'ru') {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const day = date.getDate()

  let month
  if (locale === 'ru') {
    month = monthNames[date.getMonth()]
  } else {
    month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
  }

  return `${hours}:${minutes}, ${day} ${month}`
}

/**
 * Формирует заголовок для периода "За весь период"
 * @param {number} startDay - начальный день
 * @param {number} startMonth - начальный месяц (0-11)
 * @param {number} startYear - начальный год
 * @returns {string} - форматированный заголовок периода
 */
export function formatEntirePeriodTitle(startDay, startMonth, startYear) {
  return `За весь период с ${startDay} ${monthNames[startMonth]} ${startYear}`
}

/**
 * Формирует заголовок для текущего месяца
 * @param {number} firstDay - первый день месяца
 * @param {number} lastDay - последний день месяца
 * @param {string} monthName - название месяца
 * @returns {string} - форматированный заголовок текущего месяца
 */
export function formatCurrentMonthTitle(firstDay, lastDay, monthName) {
  return `За текущий месяц с ${firstDay} по ${lastDay} ${monthName}`
}

/**
 * Рассчитывает процентное соотношение между двумя значениями
 * @param {number} val1 - первое значение (общее количество)
 * @param {number} val2 - второе значение (часть от общего количества)
 * @returns {string} - форматированное процентное значение с символом %
 */
export function calculatePercentage(val1, val2) {
  if (!val1 || !val2) return '0%'
  const percentage = (val2 / val1) * 100
  return `${Math.round(percentage)}%`
}

/**
 * Формирует строку с датой следующей выплаты
 * @param {number} payoutDay - день выплаты (по умолчанию 10 число)
 * @param {string} locale - локаль (по умолчанию 'ru')
 * @param {function} t - функция перевода
 * @returns {string} - форматированная строка следующей выплаты
 */
export function formatNextPayoutDate(payoutDay = 10, locale = 'ru', t = null) {
  const today = new Date()
  const nextMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    payoutDay
  )
  const day = nextMonth.getDate()

  let month
  if (locale === 'ru') {
    month = monthNames[nextMonth.getMonth()]
  } else {
    month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(nextMonth)
  }

  const prefix = t ? t('FinanceSection | untilPrefix') : 'До'
  return `${prefix} ${day} ${month}` // example: До 10 декабря
}

/**
 * Форматирует дату транзакции в формат "27 апреля в 12:08"
 * @param {string} dateString - строка даты в ISO формате (например, "2025-06-16T14:17:37Z")
 * @param {string} locale - локаль (по умолчанию 'ru')
 * @param {function} t - функция перевода
 * @returns {string} - форматированная строка даты
 */
export function formatTransactionDate(dateString, locale = 'ru', t = null) {
  if (!dateString) return ''

  const date = new Date(dateString)
  if (!dateString || isNaN(date.getTime()) || date === null) return ''
  const day = date.getDate()

  let month
  if (locale === 'ru') {
    month = monthNames[date.getMonth()]
  } else {
    month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
  }

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  const atPrefix = t ? t('TransactionDate | atPrefix') : 'в'
  return `${day} ${month} ${atPrefix} ${hours}:${minutes}` // example: 27 апреля в 12:08
}

/**
 * Формирует заголовок для периода "За весь период" по таймстампу
 * @param {number|object} timestamp - UNIX timestamp (секунды) или объект {seconds, nanos}
 * @param {function} t - функция перевода
 * @param {string} locale - локаль
 * @returns {string} - форматированный заголовок периода
 */
export function formatEntirePeriodTitleFromTimestamp(
  timestamp,
  t,
  locale = 'ru'
) {
  let date
  if (typeof timestamp === 'number') {
    date = new Date(timestamp * 1000)
  } else if (
    typeof timestamp === 'object' &&
    timestamp &&
    'seconds' in timestamp
  ) {
    date = new Date(timestamp.seconds * 1000 + (timestamp.nanos || 0) / 1e6)
  }
  if (date && !isNaN(date.getTime())) {
    const day = date.getDate()
    const month = date.getMonth()
    const year = date.getFullYear()
    let monthName
    if (locale === 'ru') {
      monthName = monthNames[month]
    } else {
      monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
        date
      )
    }
    const periodPrefix = t('Widget | entirePeriodPrefix')
    return `${periodPrefix} ${day} ${monthName} ${year}`
  }
  return t ? t('Widget | entirePeriodDefault') : 'За весь период'
}

/**
 * Формирует заголовок для текущего месяца по таймстампам
 * @param {number|object} startTimestamp - начало периода (UNIX timestamp или {seconds, nanos})
 * @param {number|object} endTimestamp - конец периода (UNIX timestamp или {seconds, nanos})
 * @param {function} t - функция перевода
 * @param {string} locale - локаль
 * @returns {string} - форматированный заголовок периода
 */
export function formatCurrentMonthTitleFromTimestamps(
  startTimestamp,
  endTimestamp,
  t,
  locale = 'ru'
) {
  let startDate, endDate
  if (typeof startTimestamp === 'number') {
    startDate = new Date(startTimestamp * 1000)
  } else if (
    typeof startTimestamp === 'object' &&
    startTimestamp &&
    'seconds' in startTimestamp
  ) {
    startDate = new Date(
      startTimestamp.seconds * 1000 + (startTimestamp.nanos || 0) / 1e6
    )
  }
  if (typeof endTimestamp === 'number') {
    endDate = new Date(endTimestamp * 1000)
  } else if (
    typeof endTimestamp === 'object' &&
    endTimestamp &&
    'seconds' in endTimestamp
  ) {
    endDate = new Date(
      endTimestamp.seconds * 1000 + (endTimestamp.nanos || 0) / 1e6
    )
  }
  if (
    startDate &&
    endDate &&
    !isNaN(startDate.getTime()) &&
    !isNaN(endDate.getTime())
  ) {
    const firstDay = startDate.getDate()
    const lastDay = endDate.getDate()
    let monthName
    if (locale === 'ru') {
      monthName = monthNames[startDate.getMonth()]
    } else {
      monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
        startDate
      )
    }
    const prefix = t('Widget | currentMonthPrefix')
    return `${prefix} ${firstDay}–${lastDay} ${monthName}`
  }
  return t ? t('Widget | currentMonthDefault') : 'За текущий месяц'
}

/**
 * Форматирует дату дедлайна с отображением оставшихся дней
 * @param {string} deadlineString - строка даты дедлайна в ISO формате (например, "2025-11-12T08:24:49.31Z")
 * @param {string} locale - локаль (по умолчанию 'ru')
 * @param {function} t - функция перевода
 * @returns {string} - форматированная строка вида "31 июля • Осталось 28 дней"
 */
export function formatDeadlineWithDaysLeft(
  deadlineString,
  locale = 'ru',
  t = null
) {
  if (!deadlineString) return ''

  const deadline = new Date(deadlineString)
  if (isNaN(deadline.getTime())) return ''

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const deadlineDay = new Date(deadline)
  deadlineDay.setHours(0, 0, 0, 0)

  // Рассчитываем разницу в днях
  const diffTime = deadlineDay.getTime() - today.getTime()
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  // Форматируем дату
  const day = deadline.getDate()
  let month
  if (locale === 'ru') {
    month = monthNames[deadline.getMonth()]
  } else {
    month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(deadline)
  }

  // Форматируем количество дней с правильным склонением
  let daysText
  if (daysLeft < 0) {
    const absDays = Math.abs(daysLeft)
    const daysWord = getDaysWord(absDays, locale, t)
    daysText = t
      ? t('PaymentLinks | period | expired', { days: absDays })
      : `Просрочено на ${absDays} ${daysWord}`
  } else if (daysLeft === 0) {
    daysText = t ? t('PaymentLinks | period | lastDay') : 'Последний день'
  } else {
    const daysWord = getDaysWord(daysLeft, locale, t)
    daysText = t
      ? t('PaymentLinks | period | daysLeft', { days: daysLeft })
      : `Осталось ${daysLeft} ${daysWord}`
  }

  return `${day} ${month} • ${daysText}`
}

/**
 * Возвращает правильное склонение слова "день"
 * @param {number} days - количество дней
 * @param {string} locale - локаль
 * @param {function} t - функция перевода
 * @returns {string} - правильная форма слова (день/дня/дней)
 */
function getDaysWord(days, locale = 'ru', t = null) {
  if (locale !== 'ru') {
    return days === 1 ? 'day' : 'days'
  }

  const lastDigit = days % 10
  const lastTwoDigits = days % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return t ? t('PaymentLinks | period | days') : 'дней'
  }

  if (lastDigit === 1) {
    return t ? t('PaymentLinks | period | day') : 'день'
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return t ? t('PaymentLinks | period | daysGenitive') : 'дня'
  }

  return t ? t('PaymentLinks | period | days') : 'дней'
}

/**
 * Преобразует локальную дату в UTC ISO строку для начала дня
 * @param {Date} date - объект даты
 * @returns {string} - ISO строка в формате YYYY-MM-DD (начало дня в UTC)
 */
export function toUTCStartOfDay(date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString()
}

/**
 * Преобразует локальную дату в UTC ISO строку для конца дня
 * @param {Date} date - объект даты
 * @returns {string} - ISO строка в формате YYYY-MM-DD (конец дня в UTC)
 */
export function toUTCEndOfDay(date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString()
}

/**
 * Преобразует локальную дату в строку формата YYYY-MM-DD
 * @param {Date} date - объект даты
 * @returns {string} - строка в формате YYYY-MM-DD
 */
export function toDateString(date) {
  return toUTCStartOfDay(date).split('T')[0]
}

/**
 * Форматирует дату в относительный вид: Сегодня, Вчера, 24 января или 24 января 2024
 * @param {string|Date|number} dateInput - строка даты (ISO), Date или timestamp
 * @param {string} locale - локаль (по умолчанию 'ru')
 * @param {function} t - опциональная функция перевода для "Сегодня"/"Вчера"
 * @returns {string}
 */
export function formatRelativeDate(dateInput, locale = 'ru', t = null) {
  if (dateInput == null) return ''

  const date =
    typeof dateInput === 'string' || typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput

  if (!(date instanceof Date) || isNaN(date.getTime())) return ''

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dateOnly = new Date(date)
  dateOnly.setHours(0, 0, 0, 0)

  const diffDays = Math.round((dateOnly - today) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return t ? t('Common | today') : 'Сегодня'
  }
  if (diffDays === -1) {
    return t ? t('Common | yesterday') : 'Вчера'
  }

  const day = date.getDate()
  const month =
    locale === 'ru'
      ? monthNames[date.getMonth()]
      : new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
  const year = date.getFullYear()
  const currentYear = today.getFullYear()

  if (year === currentYear) {
    return `${day} ${month}`
  }
  return `${day} ${month} ${year}`
}
